import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Card,
    CardContent,
    Stack,
    Chip,
    Skeleton,
    Snackbar,
    Alert,
    Avatar,
} from "@mui/material";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import BuildIcon from "@mui/icons-material/Build";
import {
    getISOWeek,
    getISOWeekYear,
    addDays,
    startOfISOWeek,
    isToday,
    format,
} from "date-fns";
import { useAuth } from "../contexts/AuthContext";
import WeekPicker from "../components/pickers/WeekPicker";
import ScheduleService from "../services/Schedule.service";
import ProjectDetailsDialog from "../components/Schedule/ProjectDetailsDialog";
import VehicleDetailsDialog from "../components/Schedule/VehicleDetailsDialog";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function toISOWeekString(date) {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const day = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - day);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const week = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
    return `${d.getUTCFullYear()}W${String(week).padStart(2, "0")}`;
}

function getWeekDates(isoWeek) {
    const year = parseInt(isoWeek.slice(0, 4), 10);
    const week = parseInt(isoWeek.slice(5), 10);
    const startDate = startOfISOWeek(new Date(year, 0, (week - 1) * 7 + 1));
    return Array.from({ length: 7 }, (_, i) => addDays(startDate, i));
}

function getInitials(employee) {
    if (employee.is_subcontractor && employee.company_name) {
        return employee.company_name
            .split(" ")
            .slice(0, 2)
            .map((w) => w[0])
            .join("")
            .toUpperCase();
    }
    const first = employee.first_name?.[0] || "";
    const last = employee.last_name?.[0] || "";
    return (first + last).toUpperCase();
}

function formatTime(timeStr) {
    if (!timeStr) return "";
    const [h, m] = timeStr.split(":");
    const d = new Date();
    d.setHours(parseInt(h, 10), parseInt(m, 10));
    return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

function capitalize(str) {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1);
}

function EmployeeDayCard({ employee, day, isCurrentUser }) {
    const isSub = employee.is_subcontractor;
    const displayName = isSub && employee.company_name ? employee.company_name : `${employee.first_name} ${employee.last_name}`;
    const personName = `${employee.first_name || ""} ${employee.last_name || ""}`.trim();
    const subLabel = employee.is_helper ? `Helper: ${personName}` : isSub && personName ? personName : null;
    const [selectedSegment, setSelectedSegment] = useState(null);
    const [selectedVehicle, setSelectedVehicle] = useState(null);

    if (day.holiday) {
        return (
            <Card sx={{ mb: 1, boxShadow: "none", border: 1, borderColor: "success.main", borderRadius: 2 }}>
                <CardContent sx={{ py: "10px !important" }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar src={employee.profile_image || undefined} sx={{ width: 36, height: 36, bgcolor: isCurrentUser ? "primary.main" : "action.selected", fontSize: "0.75rem", fontFamily: "Montserrat, sans-serif", fontWeight: 700, flexShrink: 0 }}>
                            {getInitials(employee)}
                        </Avatar>
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}>{displayName}</Typography>
                            <Chip size="small" label="Holiday" color="success" />
                        </Box>
                    </Stack>
                </CardContent>
            </Card>
        );
    }

    if (day.vacation) {
        return (
            <Card sx={{ mb: 1, boxShadow: "none", border: 1, borderColor: "secondary.main", borderRadius: 2 }}>
                <CardContent sx={{ py: "10px !important" }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar src={employee.profile_image || undefined} sx={{ width: 36, height: 36, bgcolor: isCurrentUser ? "primary.main" : "action.selected", fontSize: "0.75rem", fontFamily: "Montserrat, sans-serif", fontWeight: 700, flexShrink: 0 }}>
                            {getInitials(employee)}
                        </Avatar>
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}>{displayName}</Typography>
                            <Chip size="small" label="Vacation" color="secondary" />
                        </Box>
                    </Stack>
                </CardContent>
            </Card>
        );
    }

    if (day.shop) {
        return (
            <Card sx={{ mb: 1, boxShadow: "none", border: 1, borderColor: "warning.main", borderRadius: 2 }}>
                <CardContent sx={{ py: "10px !important" }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar src={employee.profile_image || undefined} sx={{ width: 36, height: 36, bgcolor: isCurrentUser ? "primary.main" : "action.selected", fontSize: "0.75rem", fontFamily: "Montserrat, sans-serif", fontWeight: 700, flexShrink: 0 }}>
                            {getInitials(employee)}
                        </Avatar>
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}>{displayName}</Typography>
                            <Chip size="small" label="Shop" color="warning" />
                        </Box>
                    </Stack>
                </CardContent>
            </Card>
        );
    }

    const projects = day.projects || [];
    const vehicles = Array.isArray(day.vehicles)
        ? day.vehicles
        : day.vehicle
          ? [day.vehicle]
          : [];

    return (
        <Card
            sx={{
                mb: 1,
                boxShadow: "none",
                border: isSub ? 2 : 1,
                borderColor: isCurrentUser ? "primary.main" : isSub ? "warning.main" : "divider",
                borderRadius: 2,
            }}
        >
            <CardContent sx={{ py: "10px !important" }}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Avatar
                        src={employee.profile_image || undefined}
                        sx={{
                            width: 36,
                            height: 36,
                            bgcolor: isSub ? "warning.main" : isCurrentUser ? "primary.main" : "action.selected",
                            fontSize: "0.75rem",
                            fontFamily: "Montserrat, sans-serif",
                            fontWeight: 700,
                            flexShrink: 0,
                            mt: 0.25,
                        }}
                    >
                        {getInitials(employee)}
                    </Avatar>
                    <Box flex={1} minWidth={0}>
                        <Stack direction="row" alignItems="center" spacing={0.75} mb={projects.length > 0 ? 0.75 : 0}>
                            <Typography
                                variant="subtitle2"
                                sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, lineHeight: 1.3 }}
                            >
                                {displayName}
                            </Typography>
                            {isSub && (
                                <Chip label="SUB" size="small" color="warning" sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, fontSize: "0.6rem", height: 18 }} />
                            )}
                        </Stack>
                        {subLabel && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "Montserrat, sans-serif", display: "block", mt: -0.5, mb: 0.5 }}>
                                {subLabel}
                            </Typography>
                        )}

                        {projects.length === 0 && vehicles.length === 0 && (
                            <Typography variant="body2" color="text.secondary">Not scheduled</Typography>
                        )}

                        {/* Project blocks */}
                        <Stack spacing={0.75}>
                            {projects.map((p, i) => {
                                const detail = p.project_detail || p.quote_detail || {};
                                const number = detail.number || "";
                                const projName = detail.name || "";
                                const startTime = p.start_time ? formatTime(p.start_time) : null;
                                const location = p.start_location ? capitalize(p.start_location) : null;
                                return (
                                    <Box
                                        key={i}
                                        onClick={() => setSelectedSegment(p)}
                                        sx={{
                                            p: 1,
                                            borderRadius: 1.5,
                                            bgcolor: "background.paper",
                                            border: 1,
                                            borderColor: p.is_testing ? "warning.main" : "divider",
                                            borderLeft: 4,
                                            borderLeftColor: p.is_testing ? "warning.main" : "primary.main",
                                            cursor: "pointer",
                                            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                                            "&:active": { opacity: 0.7 },
                                        }}
                                    >
                                        <Stack direction="row" spacing={0.75} alignItems="center" mb={0.5}>
                                            <Box
                                                sx={{
                                                    bgcolor: p.is_testing ? "warning.main" : "primary.main",
                                                    borderRadius: 0.75,
                                                    px: 0.75,
                                                    py: 0.25,
                                                    flexShrink: 0,
                                                    display: "flex",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    sx={{ color: p.is_testing ? "warning.contrastText" : "primary.contrastText", fontFamily: "Montserrat, sans-serif", fontWeight: 700, fontSize: "0.68rem", lineHeight: 1 }}
                                                >
                                                    {number}
                                                </Typography>
                                            </Box>
                                            <Typography
                                                variant="caption"
                                                sx={{ color: "text.primary", fontFamily: "Montserrat, sans-serif", fontWeight: 700, fontSize: "0.78rem" }}
                                                noWrap
                                            >
                                                {projName}
                                            </Typography>
                                        </Stack>
                                        <Stack direction="row" alignItems="center" justifyContent="space-between" mt={0.25}>
                                            {(startTime || location) ? (
                                                <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem", pl: 0.25 }}>
                                                    {startTime && `Start: ${startTime}`}
                                                    {startTime && location ? "  ·  " : ""}
                                                    {location}
                                                </Typography>
                                            ) : <Box />}
                                            {p.is_testing && (
                                                <Chip label="Testing" size="small" color="warning" sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, fontSize: "0.6rem", height: 18 }} />
                                            )}
                                        </Stack>
                                    </Box>
                                );
                            })}
                        </Stack>

                        {/* Vehicle blocks */}
                        {vehicles.length > 0 && (
                            <Stack spacing={0.5} sx={{ mt: projects.length > 0 ? 0.75 : 0 }}>
                                {vehicles.map((v, i) => {
                                    const vName = typeof v === "string"
                                        ? v
                                        : v.nickname || v.name || v.vehicle_detail?.nickname || "";
                                    return vName ? (
                                        <Box
                                            key={i}
                                            onClick={() => setSelectedVehicle(typeof v === "string" ? null : v)}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.75,
                                                p: 1,
                                                borderRadius: 1.5,
                                                bgcolor: "background.paper",
                                                border: 1,
                                                borderColor: "divider",
                                                borderLeft: 4,
                                                borderLeftColor: "secondary.main",
                                                cursor: "pointer",
                                                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                                                "&:active": { opacity: 0.7 },
                                            }}
                                        >
                                            <DirectionsCarIcon sx={{ fontSize: "1rem", color: "secondary.main", flexShrink: 0 }} />
                                            <Typography variant="caption" sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, fontSize: "0.78rem", color: "text.primary", flex: 1 }}>
                                                {vName}
                                            </Typography>
                                            {v.vehicle_detail?.open_issues_count > 0 && (
                                                <WarningAmberIcon sx={{ fontSize: "0.9rem", color: "error.main", flexShrink: 0 }} />
                                            )}
                                            {v.vehicle_detail?.expired_inspections_count > 0 && (
                                                <BuildIcon sx={{ fontSize: "0.9rem", color: "warning.main", flexShrink: 0 }} />
                                            )}
                                        </Box>
                                    ) : null;
                                })}
                            </Stack>
                        )}
                    </Box>
                </Stack>
            </CardContent>

            <ProjectDetailsDialog segment={selectedSegment} onClose={() => setSelectedSegment(null)} />
            <VehicleDetailsDialog vehicleSegment={selectedVehicle} onClose={() => setSelectedVehicle(null)} />
        </Card>
    );
}

function dayHasContent(day) {
    return !!(
        day.holiday ||
        day.vacation ||
        day.shop ||
        (day.projects && day.projects.length > 0) ||
        (Array.isArray(day.vehicles) ? day.vehicles.length > 0 : !!day.vehicle)
    );
}

export default function FieldSchedule() {
    const { user } = useAuth();
    const [isoWeek, setIsoWeek] = useState(() => toISOWeekString(new Date()));
    const [weekData, setWeekData] = useState([]);
    const [dayIndex, setDayIndex] = useState(0);
    const [loading, setLoading] = useState(false);
    const [snack, setSnack] = useState({ open: false, message: "", severity: "error" });
    const [holidayMap, setHolidayMap] = useState({});

    const weekDates = getWeekDates(isoWeek);

    // Sync dayIndex to today when the week changes
    useEffect(() => {
        const today = new Date();
        const currentWeek = getISOWeek(today);
        const currentYear = getISOWeekYear(today);
        const selWeek = parseInt(isoWeek.slice(5), 10);
        const selYear = parseInt(isoWeek.slice(0, 4), 10);

        if (selWeek === currentWeek && selYear === currentYear) {
            const dow = today.getDay();
            setDayIndex(dow === 0 ? 6 : dow - 1);
        } else {
            setDayIndex(0);
        }
    }, [isoWeek]);

    useEffect(() => {
        const weekDatesLocal = getWeekDates(isoWeek);
        const years = [...new Set(weekDatesLocal.map((d) => d.getFullYear()))];
        Promise.all(years.map((y) => ScheduleService.getHolidaysByYear(y)))
            .then((results) => {
                const map = {};
                results.forEach((res) => {
                    (res.data || []).forEach((h) => {
                        if (h.date) {
                            map[h.date] = {
                                name: h.holiday_name?.name || "Holiday",
                                halfDay: !!h.half_day,
                            };
                        }
                    });
                });
                setHolidayMap(map);
            })
            .catch(() => {});
    }, [isoWeek]);

    useEffect(() => {
        setLoading(true);
        ScheduleService.getScheduleByWeek(isoWeek)
            .then((res) => {
                const data = res.data;
                const matrix = Array.isArray(data)
                    ? data
                    : Array.isArray(data?.matrix)
                      ? data.matrix
                      : [];

                // Merge profile_image, is_subcontractor, company_name from
                // selectedUser. Login-less subcontractors and helpers have no
                // user_detail, so their row is built from selectedUser alone.
                const rowMap = {};
                (data?.selectedUser || []).forEach((u) => {
                    if (u.row_key) rowMap[u.row_key] = u;
                });
                const enriched = matrix.map((entry) => {
                    const extra = rowMap[entry.row_key] || {};
                    if (!entry.user_detail && !extra.row_key) return entry;
                    return {
                        ...entry,
                        user_detail: {
                            id: entry.user_detail?.id ?? null,
                            first_name: entry.user_detail?.first_name ?? extra.first_name ?? "",
                            last_name: entry.user_detail?.last_name ?? extra.last_name ?? "",
                            row_key: entry.row_key,
                            profile_image: extra.profile_image || null,
                            is_subcontractor: extra.is_subcontractor || false,
                            is_helper: extra.is_helper || false,
                            company_name: extra.company_name || "",
                        },
                    };
                });

                setWeekData(enriched);
            })
            .catch((err) => {
                console.error("Schedule API error:", err);
                setSnack({ open: true, message: "Failed to load schedule.", severity: "error" });
            })
            .finally(() => setLoading(false));
    }, [isoWeek]);

    // Sort: current user first, then by project name, special statuses last
    const sortedData = [...weekData].filter((e) => e?.user_detail).sort((a, b) => {
        if (a.user_detail.id && a.user_detail.id === user?.id) return -1;
        if (b.user_detail.id && b.user_detail.id === user?.id) return 1;

        const dayA = a.days[dayIndex];
        const dayB = b.days[dayIndex];
        const specialA = dayA && (dayA.shop || dayA.holiday || dayA.vacation);
        const specialB = dayB && (dayB.shop || dayB.holiday || dayB.vacation);

        if (specialA && !specialB) return 1;
        if (!specialA && specialB) return -1;

        const projA = dayA?.projects?.[0]?.name || "";
        const projB = dayB?.projects?.[0]?.name || "";
        return projA.localeCompare(projB);
    });

    // The matrix carries every employee/sub for every day (even blank ones)
    // so a row with nothing on it (no project, vehicle, holiday, vacation,
    // or shop) is just noise - only people with something actually going on
    // that day belong in the list.
    const visibleRows = sortedData
        .map(({ user_detail, days }) => ({ user_detail, day: days[dayIndex] }))
        .filter(({ day }) => !!day && dayHasContent(day));

    const formattedDate = format(weekDates[dayIndex], "EEEE, MMMM do");
    const currentHoliday = holidayMap[format(weekDates[dayIndex], "yyyy-MM-dd")] || null;

    return (
        <Box sx={{ maxWidth: 600, mx: "auto", pb: 10 }}>
            {/* Week picker */}
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                <WeekPicker isoWeek={isoWeek} getIsoWeek={setIsoWeek} />
            </Box>

            {/* Day selector */}
            <Box sx={{ display: "flex", mb: 1.5, gap: "3px" }}>
                {weekDates.map((date, i) => {
                    const selected = i === dayIndex;
                    const todayDay = isToday(date);
                    const isHoliday = !!holidayMap[format(date, "yyyy-MM-dd")];
                    return (
                        <Box
                            key={i}
                            onClick={() => setDayIndex(i)}
                            sx={{
                                flex: 1,
                                py: 0.75,
                                borderRadius: 1.5,
                                textAlign: "center",
                                cursor: "pointer",
                                border: isHoliday && !selected ? 2 : 1,
                                borderColor: selected
                                    ? "primary.main"
                                    : isHoliday
                                      ? "success.main"
                                      : todayDay
                                        ? "secondary.main"
                                        : "divider",
                                bgcolor: selected ? "primary.main" : "transparent",
                                transition: "all 0.15s",
                            }}
                        >
                            <Typography
                                variant="caption"
                                display="block"
                                sx={{
                                    fontFamily: "Montserrat, sans-serif",
                                    fontWeight: 700,
                                    fontSize: "0.6rem",
                                    color: selected ? "#fff" : "text.secondary",
                                    lineHeight: 1.2,
                                }}
                            >
                                {DAY_LABELS[i]}
                            </Typography>
                            <Typography
                                variant="caption"
                                display="block"
                                sx={{
                                    fontFamily: "Montserrat, sans-serif",
                                    fontWeight: selected ? 700 : 400,
                                    fontSize: "0.75rem",
                                    color: selected
                                        ? "#fff"
                                        : todayDay
                                          ? "secondary.main"
                                          : "text.primary",
                                }}
                            >
                                {format(date, "d")}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>

            <Typography
                variant="subtitle2"
                color="text.secondary"
                align="center"
                sx={{ mb: 2, fontFamily: "Montserrat, sans-serif" }}
            >
                {formattedDate}
            </Typography>

            {currentHoliday && (
                <Card sx={{ mb: 2, boxShadow: "none", border: 2, borderColor: "success.main", borderRadius: 2 }}>
                    <CardContent sx={{ py: "12px !important" }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <BeachAccessIcon sx={{ fontSize: "2rem", color: "success.main", flexShrink: 0 }} />
                            <Box flex={1} minWidth={0}>
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontFamily: "Montserrat, sans-serif",
                                        fontWeight: 800,
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {currentHoliday.name}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontFamily: "Montserrat, sans-serif" }}
                                >
                                    {currentHoliday.halfDay
                                        ? "Office Closed · Half Day"
                                        : "Office Closed · Company Holiday"}
                                </Typography>
                            </Box>
                            <Chip
                                label={currentHoliday.halfDay ? "Half Day" : "Holiday"}
                                color="success"
                                size="small"
                                sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}
                            />
                        </Stack>
                    </CardContent>
                </Card>
            )}

            {/* Employee list */}
            {loading ? (
                [1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} variant="rounded" height={62} sx={{ mb: 1, borderRadius: 2 }} />
                ))
            ) : weekData.length === 0 ? (
                <Box sx={{ textAlign: "center", mt: 6 }}>
                    <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, mb: 0.5 }}
                    >
                        No Schedule Posted
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Please check back later
                    </Typography>
                </Box>
            ) : visibleRows.length === 0 ? (
                <Box sx={{ textAlign: "center", mt: 6 }}>
                    <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, mb: 0.5 }}
                    >
                        Nothing Scheduled
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        You have no projects on this day
                    </Typography>
                </Box>
            ) : (
                visibleRows.map(({ user_detail, day }) => (
                    <EmployeeDayCard
                        key={user_detail.row_key || user_detail.id}
                        employee={user_detail}
                        day={day}
                        isCurrentUser={!!user_detail.id && user_detail.id === user?.id}
                    />
                ))
            )}

            <Snackbar
                open={snack.open}
                autoHideDuration={4000}
                onClose={() => setSnack((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    severity={snack.severity}
                    onClose={() => setSnack((s) => ({ ...s, open: false }))}
                >
                    {snack.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
